import { posthog, PostHogConfig } from "posthog-js";

import { getCurrentPreferences } from "../modules/CookieConsent/utils/consentManager";
import { BUILD_ENV } from "../shared/constants/environment";
import { shouldSendAnalyticsCall } from "./analyticsGate";

const POSTHOG_API_HOST = "https://us.i.posthog.com";
const POSTHOG_UI_HOST = "https://us.posthog.com";
const POSTHOG_RECORDING_HOSTS_BY_ENV: Record<string, Set<string>> = {
  integration: new Set([
    "integration-my.wingspan.app",
    "integration-onboarder.wingspan.app"
  ]),
  production: new Set(["my.wingspan.app", "onboarder.wingspan.app"]),
  staging: new Set([
    "staging-my.wingspan.app",
    "staging-onboarder.wingspan.app",
    "staging-02-my.wingspan.app"
  ])
};

let isPostHogInitialized = false;
let identifiedPostHogUserId: string | undefined;

export interface PostHogEligibility {
  analyticsConsent: boolean;
  buildEnv?: string;
  hostname: string;
  projectToken?: string;
  shouldSendAnalytics: boolean;
}

/**
 * Returns whether the current browser context may initialize PostHog replay.
 */
export const isPostHogEligible = ({
  analyticsConsent,
  buildEnv,
  hostname,
  projectToken,
  shouldSendAnalytics
}: PostHogEligibility): boolean =>
  !!projectToken?.match(/^phc_[A-Za-z0-9]+$/) &&
  analyticsConsent &&
  shouldSendAnalytics &&
  !!buildEnv &&
  !!POSTHOG_RECORDING_HOSTS_BY_ENV[buildEnv]?.has(hostname);

/**
 * Builds privacy defaults that remain active if remote project configuration
 * is delayed or temporarily unavailable.
 */
export const getPostHogConfig = (): Partial<PostHogConfig> => ({
  api_host: POSTHOG_API_HOST,
  ui_host: POSTHOG_UI_HOST,
  defaults: "2026-06-25",
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: false,
  enable_recording_console_log: true,
  person_profiles: "identified_only",
  session_recording: {
    blockSelector: ".fs-exclude, .ph-no-capture",
    maskAllInputs: true
  }
});

/**
 * Initializes PostHog session replay once consent, environment, and internal
 * traffic exclusions have all been satisfied.
 */
export const loadPostHog = (): void => {
  if (isPostHogInitialized) {
    return;
  }

  const projectToken = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
  const { C0002AL: analyticsConsent } = getCurrentPreferences();
  const isEligible = isPostHogEligible({
    analyticsConsent,
    buildEnv: BUILD_ENV,
    hostname: window.location.hostname,
    projectToken,
    shouldSendAnalytics: shouldSendAnalyticsCall()
  });

  if (!isEligible || !projectToken) {
    return;
  }

  try {
    posthog.init(projectToken, getPostHogConfig());
    isPostHogInitialized = true;
  } catch {
    console.error("Failed to initialize PostHog");
  }
};

/**
 * Associates replay with the stable Wingspan user ID without sending profile
 * traits or other directly identifying fields from this client integration.
 */
export const identifyPostHog = (userId?: string): void => {
  if (!isPostHogInitialized || !userId) {
    return;
  }

  if (identifiedPostHogUserId === userId) {
    return;
  }

  if (identifiedPostHogUserId) {
    posthog.stopSessionRecording();
    posthog.reset();
  }

  posthog.startSessionRecording();
  posthog.identify(userId);
  identifiedPostHogUserId = userId;
};

/**
 * Ends the current replay and clears the PostHog identity at logout.
 */
export const resetPostHog = (): void => {
  if (!isPostHogInitialized) {
    return;
  }

  posthog.stopSessionRecording();
  posthog.reset();
  identifiedPostHogUserId = undefined;
};

/**
 * Returns a support-ready replay URL positioned shortly before the current
 * error, or an empty string when no PostHog recording is available.
 */
export const getPostHogSessionReplayUrl = (): string => {
  if (!isPostHogInitialized) {
    return "";
  }

  return posthog.get_session_replay_url({
    withTimestamp: true,
    timestampLookBack: 30
  });
};
